import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  type CognitoTokens,
  type CognitoUser,
  clearTokens,
  getLogoutUrl,
  getUserFromIdToken,
  handleCallback,
  isTokenExpired,
  loadTokens,
  redirectToLogin,
  refreshTokens,
  storeTokens,
  takeReturnTo,
  isCognitoReturn,
} from './cognito.js';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: CognitoUser | null;
  tokens: CognitoTokens | null;
}

interface AuthContextValue extends AuthState {
  login: (returnTo?: string) => Promise<void>;
  logout: () => void;
  getIdToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_MARGIN_MS = 5 * 60 * 1000;

/**
 * The local preview (#922): `npm run preview:mock` builds with VITE_PREVIEW, and on loopback only the app is signed in
 * as a preview operator, with no Cognito at all; the API answers from fixtures. Never in a real build.
 */
const PREVIEW = import.meta.env.VITE_PREVIEW === '1' && ['127.0.0.1', 'localhost', '[::1]'].includes(window.location.hostname);
const PREVIEW_STATE: AuthState = {
  isAuthenticated: true, isLoading: false,
  user: { sub: 'preview', email: 'operator@preview.test', org: 'fd-management', role: 'admin' },
  tokens: { accessToken: 'preview', idToken: 'preview', refreshToken: '', expiresAt: Number.MAX_SAFE_INTEGER },
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(PREVIEW ? PREVIEW_STATE : {
    isAuthenticated: false,
    isLoading: true,
    user: null,
    tokens: null,
  });

  const setAuthenticated = useCallback((tokens: CognitoTokens) => {
    const user = getUserFromIdToken(tokens.idToken);
    storeTokens(tokens);
    setState({ isAuthenticated: true, isLoading: false, user, tokens });
  }, []);

  useEffect(() => {
    if (PREVIEW) return;
    const init = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');

      // Only the return of our own Cognito sign-in: Google's consent comes back with a code too (#763), and
      // exchanging it with Cognito failed and signed the operator out
      if (code && isCognitoReturn()) {
        try {
          const tokens = await handleCallback(code);
          setAuthenticated(tokens);
          // Back to the page asked for before signing in (e.g. a notification's deep link)
          window.history.replaceState({}, '', takeReturnTo() ?? window.location.pathname);
          return;
        } catch {
          clearTokens();
        }
      }

      const stored = loadTokens();
      if (stored) {
        if (isTokenExpired(stored)) {
          try {
            const refreshed = await refreshTokens(stored.refreshToken);
            setAuthenticated(refreshed);
            return;
          } catch {
            clearTokens();
          }
        } else {
          setAuthenticated(stored);
          return;
        }
      }

      setState((s) => ({ ...s, isLoading: false }));
    };

    init();
  }, [setAuthenticated]);

  useEffect(() => {
    // The preview's token never expires, and its delay would overflow setTimeout
    if (!state.tokens || PREVIEW) return;

    const timeUntilRefresh = state.tokens.expiresAt - Date.now() - REFRESH_MARGIN_MS;
    if (timeUntilRefresh <= 0) return;

    const timer = setTimeout(async () => {
      try {
        const refreshed = await refreshTokens(state.tokens!.refreshToken);
        setAuthenticated(refreshed);
      } catch {
        clearTokens();
        setState({ isAuthenticated: false, isLoading: false, user: null, tokens: null });
      }
    }, timeUntilRefresh);

    return () => clearTimeout(timer);
  }, [state.tokens, setAuthenticated]);

  const login = useCallback(async (returnTo?: string) => {
    await redirectToLogin(returnTo);
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setState({ isAuthenticated: false, isLoading: false, user: null, tokens: null });
    window.location.href = getLogoutUrl();
  }, []);

  const getIdToken = useCallback(async (): Promise<string> => {
    if (!state.tokens) throw new Error('Not authenticated');

    if (isTokenExpired(state.tokens)) {
      const refreshed = await refreshTokens(state.tokens.refreshToken);
      setAuthenticated(refreshed);
      return refreshed.idToken;
    }

    return state.tokens.idToken;
  }, [state.tokens, setAuthenticated]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, getIdToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
